import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  multiFactor,
  onIdTokenChanged,
  signOut as firebaseSignOut,
  type ParsedToken,
  type User,
} from "firebase/auth";
import { firebaseAuth } from "@/shared/lib/firebase";
import { crashReporting } from "@/shared/lib/crash-reporting";
import { queryClient } from "@/shared/lib/query-client";
import {
  AuthContext,
  type AuthContextValue,
  type AuthRejection,
  type SessionUser,
} from "@/shared/auth/auth-context";
import { parsePermissions } from "./parse-permissions";
import { parseRole } from "./parse-role";
import { classifyVerificationError, verifyBackendUser } from "./verify-session";

function toSessionUser(
  firebaseUser: User,
  claims: ParsedToken,
  permissions: readonly string[],
): SessionUser {
  const role = parseRole(claims.role);
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    role,
    mfaEnrolled: multiFactor(firebaseUser).enrolledFactors.length > 0,
    permissions,
  };
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthContextValue["status"]>("loading");
  const [user, setUser] = useState<SessionUser | null>(null);
  const [rejection, setRejection] = useState<AuthRejection | null>(null);

  // Race guard for the async onIdTokenChanged listener. Each invocation
  // reserves a monotonically increasing generation; when an awaited step
  // (token resolution, backend verification) resolves, we only apply state if
  // our generation is still the latest. Protects against this sequence:
  //   1. event A (user) fires, awaits getIdTokenResult() / verifyBackendUser()
  //   2. event B (null — logout/expiry) fires, applies unauthenticated
  //   3. A's await resolves late and would otherwise clobber state back
  //      to authenticated with a stale session
  const generationRef = useRef(0);

  useEffect(() => {
    // A new subscription is a clean slate.
    generationRef.current = 0;

    const unsubscribe = onIdTokenChanged(firebaseAuth, async (firebaseUser) => {
      const generation = ++generationRef.current;
      const isLatest = () => generation === generationRef.current;

      if (!firebaseUser) {
        if (!isLatest()) return;
        // The query cache is a module singleton that outlives the session:
        // drop it so one operator's permissioned data (e.g. offline-acceptance
        // records) is never served to the next person who signs in.
        queryClient.clear();
        setUser(null);
        setStatus("unauthenticated");
        // Intentionally do NOT clear `rejection` here: a `not-provisioned`
        // rejection signs the user out, which fires this null event, and we
        // need the reason to survive so LoginPage can explain the bounce.
        return;
      }

      // A fresh Firebase session starts verification from a clean slate —
      // clear any stale rejection from a previous attempt.
      if (isLatest()) setRejection(null);

      let claims: ParsedToken;
      try {
        const tokenResult = await firebaseUser.getIdTokenResult();
        claims = tokenResult.claims;
      } catch (err) {
        if (!isLatest()) return;
        console.error("Failed to resolve Firebase ID token", err);
        setUser(null);
        setStatus("unauthenticated");
        setRejection("verification-failed");
        return;
      }

      // A valid Firebase token is necessary but NOT sufficient: confirm the
      // user exists in the backend DB before granting entry. Without this, a
      // Firebase user with no backend account would pass login and then hit
      // failing requests on every screen.
      //
      // Design decision — fail closed on EVERY token event (initial sign-in
      // AND every silent token refresh), not just on first session
      // establishment. If the backend is unreachable during a refresh while an
      // operator is mid-session, the next event denies access rather than
      // trusting a session we can no longer confirm. We accept the aggressive
      // UX (a backend blip can bounce an active operator to /login with a
      // retry-able "verification-failed") in exchange for never serving an
      // unverifiable session. Revisit if it generates support load.
      //
      // Besides the existence check, the payload supplies `permissions`
      // (v2.0 A8), the source of truth for permission-gated UI. The rest of
      // SessionUser is still built from Firebase claims below; consuming the
      // other backend fields (displayName / mfaEnrolled per API-0.4) is
      // deferred — see docs/api-requirements.md.
      let backendUser: Awaited<ReturnType<typeof verifyBackendUser>>;
      try {
        backendUser = await verifyBackendUser();
      } catch (err) {
        if (!isLatest()) return;
        const reason = classifyVerificationError(err);
        setUser(null);
        setStatus("unauthenticated");
        setRejection(reason);
        // `not-provisioned` is terminal — drop the Firebase session so no stale
        // token lingers. `verification-failed` is transient — keep the session
        // so a reload re-fires this listener and re-verifies without a new login.
        if (reason === "not-provisioned") {
          try {
            await firebaseSignOut(firebaseAuth);
          } catch (signOutErr) {
            // A failed sign-out leaves the Firebase session live, which would
            // re-fire this listener and loop on re-verification. Report it; the
            // UI already reflects the not-provisioned state regardless.
            crashReporting.recordError(
              signOutErr instanceof Error ? signOutErr : new Error(String(signOutErr)),
              { endpoint: "firebase/signOut", reason: "not-provisioned-cleanup" },
            );
          }
        }
        return;
      }

      if (!isLatest()) return;
      setUser(toSessionUser(firebaseUser, claims, parsePermissions(backendUser.permissions)));
      setStatus("authenticated");
      setRejection(null);
    });

    return () => {
      // Bump the generation so any in-flight async work bails on resolution.
      generationRef.current = Number.MAX_SAFE_INTEGER;
      unsubscribe();
    };
  }, []);

  const signOut = async () => {
    setRejection(null);
    await firebaseSignOut(firebaseAuth);
  };

  return (
    <AuthContext.Provider value={{ status, user, rejection, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
